import React, { useState, useCallback, useMemo } from 'react';
import { Play, Clock } from 'lucide-react';
import { MockVideo } from '@/hooks/video-generator/useVideoGeneration';
import VideoLightbox from '../video-lightbox/VideoLightbox';
import GlassPill from '@/components/ui/GlassPill';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';

interface VideoCardProps {
  video: MockVideo;
  onSelect: (video: MockVideo) => void;
  onLike: (video: MockVideo) => void;
  allVideos?: MockVideo[];
  onNavigate?: (video: MockVideo) => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, onSelect, onLike, allVideos = [], onNavigate }) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(video.isLiked || false); // Use video's like status
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleCardClick = useCallback(() => {
    setIsLightboxOpen(true);
    onSelect(video);
  }, [onSelect, video]);

  const handleLikeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    onLike(video);
  }, [isLiked, onLike, video]);

  const handleToggleFavorite = useCallback(async (itemId: string): Promise<void> => {
    setIsLiked(!isLiked);
    onLike(video);
  }, [isLiked, onLike, video]);

  // Memoize duration formatting to prevent recalculation
  const formattedDuration = useMemo(() => {
    const minutes = Math.floor(video.duration / 60);
    const remainingSeconds = Math.floor(video.duration % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, [video.duration]);

  return (
    <>
      <div 
        className="group relative bg-card rounded-lg overflow-hidden border border-border hover:border-purple-200 transition-all duration-200 cursor-pointer"
        onClick={handleCardClick}
      >
      {/* Video Thumbnail */}
      <div className="relative aspect-video bg-muted">
        {/* Loading placeholder */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-muted/30 animate-pulse flex items-center justify-center">
            <div className="text-muted-foreground text-xs">Loading...</div>
          </div>
        )}
        
        <img 
          src={video.thumbnail} 
          alt={video.title}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />
        
        {/* Play Overlay */}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="bg-white/90 rounded-full p-3 backdrop-blur-sm">
            <Play className="h-6 w-6 text-gray-900 ml-0.5" fill="currentColor" />
          </div>
        </div>

        {/* Audio Indicator - Top Left (only show for videos with audio) */}
        {video.hasAudio && (
          <div className="absolute top-2 left-2">
            <GlassPill
              type="With Audio"
              variant="blue"
              className="text-xs px-2 py-1"
            />
          </div>
        )}

        {/* Like Button */}
        <div className="absolute top-2 right-2">
          <PersistentFavoriteButton
            itemId={video.id}
            onToggleFavorite={handleToggleFavorite}
            isFavorite_prop={isLiked}
            favoriteCount_prop={video.likeCount || 0}
            forceWhiteText={true}
            className="transform scale-90 origin-top-right"
          />
        </div>

        {/* Duration */}
        <div className="absolute bottom-2 right-2 bg-black/60 rounded px-2 py-1 backdrop-blur-sm">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-white" />
            <span className="text-xs text-white font-medium">
              {formattedDuration}
            </span>
          </div>
        </div>


      </div>

      {/* Video Info */}
      <div className="p-3">
        <h3 className="font-medium text-sm mb-1 line-clamp-1">{video.title}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {video.originalPrompt}
        </p>

      </div>
      </div>
      
      <VideoLightbox
        video={video}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        allVideos={allVideos}
        onNavigate={onNavigate}
      />
    </>
  );
};

// Memoize VideoCard to prevent unnecessary re-renders
export default React.memo(VideoCard, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when props haven't meaningfully changed
  return (
    prevProps.video.id === nextProps.video.id &&
    prevProps.video.isLiked === nextProps.video.isLiked &&
    prevProps.video.likeCount === nextProps.video.likeCount &&
    prevProps.allVideos.length === nextProps.allVideos.length
  );
}); 